import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, X, Megaphone, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { ThemeToggle } from './ThemeToggle';
import { motion, AnimatePresence } from 'framer-motion';
import { publicApi } from '../lib/api';

import { useApp } from '../context/AppContext';

export default function Navbar() {
  const { user, logout, cartCount } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [settings, setSettings] = useState({ announcement_enabled: false, announcement_message: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await publicApi.getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const navItemClass = (path) =>
    `px-4 py-2 rounded-full transition-all duration-300 ${
      isActive(path)
        ? 'bg-white/10 text-white border border-white/20'
        : 'text-foreground/90 hover:text-white hover:bg-white/5'
    }`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-background/70 backdrop-blur-xl" data-testid="main-navbar">
      {settings.announcement_enabled && settings.announcement_message && location.pathname === '/' && (
        <div className="bg-primary text-white py-2.5 px-4 overflow-hidden relative shadow-[0_0_20px_rgba(139,92,246,0.3)]">
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 text-sm font-semibold tracking-wide"
          >
            <Megaphone className="h-4 w-4 animate-bounce" />
            <p className="uppercase tracking-widest">{settings.announcement_message}</p>
            <Megaphone className="h-4 w-4 animate-bounce scale-x-[-1]" />
          </motion.div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link" onClick={closeMenu}>
            <img 
              src="https://customer-assets.emergentagent.com/job_threedy-market-7/artifacts/urt0p1iq_file_00000000b95471fda339a02d2c655452.png" 
              alt="Ved3dCreations" 
              className="h-10 md:h-12 w-auto"
              style={{ filter: 'url(#remove-white)' }}
            />
            <span className="text-xl md:text-2xl font-bold tracking-tight animate-gradient-text">Ved3dCreations</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-3">
            <ThemeToggle />
            <div className="glass-panel rounded-full px-2 py-1.5 flex items-center gap-1.5">
              <Link to="/products" data-testid="products-link" className={navItemClass('/products')}>
                Products
              </Link>

              {settings.referral_enabled !== false && (
                <Link to="/referral" data-testid="referral-link" className={navItemClass('/referral')}>
                  Refer & Earn
                </Link>
              )}

              <Link to="/contact" data-testid="contact-link" className={navItemClass('/contact')}>
                Contact
              </Link>
            </div>

            {user ? (
              <>
                <div className="glass-panel rounded-full px-2 py-1.5 flex items-center gap-1.5">
                  {user.is_admin ? (
                    <>
                      <Link to="/admin" data-testid="admin-dashboard-link" className={navItemClass('/admin')}>
                        <span className="inline-flex items-center">
                          <LayoutDashboard className="h-4 w-4 mr-2" />
                          Dashboard
                        </span>
                      </Link>
                      <Link to="/admin/chat" data-testid="admin-chat-link" className={navItemClass('/admin/chat')}>
                        <span className="inline-flex items-center">
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Chat
                        </span>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/cart" data-testid="cart-link" className={`${navItemClass('/cart')} relative`}>
                        <ShoppingCart className="h-5 w-5" />
                        <AnimatePresence>
                          {cartCount > 0 && (
                            <motion.span
                              key={cartCount}
                              initial={{ scale: 0.5, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.5, opacity: 0 }}
                              className="absolute -top-1 -right-1 bg-secondary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
                              data-testid="cart-count"
                            >
                              {cartCount}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </Link>
                      <Link to="/orders" data-testid="orders-link" className={navItemClass('/orders')}>
                        Orders
                      </Link>
                      <Link to="/profile" data-testid="profile-link" className={navItemClass('/profile')}>
                        Profile
                      </Link>
                    </>
                  )}
                </div>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-foreground hover:text-destructive transition-colors duration-300"
                  data-testid="logout-button"
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <Button asChild className="rounded-full bg-primary hover:bg-primary/90 text-white px-6 shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] hover:-translate-y-1 transition-all duration-300">
                <Link to="/login" data-testid="login-link">
                  <User className="h-5 w-5 mr-2" />
                  Login
                </Link>
              </Button>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex lg:hidden items-center gap-4">
            <ThemeToggle />
            <Button variant="ghost" size="icon" onClick={toggleMenu} aria-label="Toggle menu">
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden border-t border-white/10 overflow-hidden surface-elevated backdrop-blur-2xl"
          >
            <div className="px-6 py-8 flex flex-col gap-4">
              <Link 
                to="/products" 
                className="text-lg font-medium hover:text-primary transition-colors"
                onClick={closeMenu}
              >
                Products
              </Link>
              {settings.referral_enabled !== false && (
                <Link 
                  to="/referral" 
                  className="text-lg font-medium hover:text-primary transition-colors"
                  onClick={closeMenu}
                >
                  Refer & Earn
                </Link>
              )}
              <Link 
                to="/contact" 
                className="text-lg font-medium hover:text-primary transition-colors"
                onClick={closeMenu}
              >
                Contact
              </Link>
              
              <div className="h-px bg-white/10 my-2" />

              {user ? (
                <>
                  {user.is_admin ? (
                    <>
                      <Link 
                        to="/admin" 
                        className="text-lg font-medium flex items-center gap-3 hover:text-primary transition-colors"
                        onClick={closeMenu}
                      >
                        <LayoutDashboard className="h-5 w-5" />
                        Dashboard
                      </Link>
                      <Link 
                        to="/admin/chat" 
                        className="text-lg font-medium flex items-center gap-3 hover:text-primary transition-colors"
                        onClick={closeMenu}
                      >
                        <MessageCircle className="h-5 w-5" />
                        Chat
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link 
                        to="/cart" 
                        className="text-lg font-medium flex items-center justify-between hover:text-primary transition-colors"
                        onClick={closeMenu}
                      >
                        <span className="flex items-center gap-3">
                          <ShoppingCart className="h-5 w-5" />
                          Cart
                        </span>
                        <AnimatePresence>
                          {cartCount > 0 && (
                            <motion.span 
                              key={cartCount}
                              initial={{ scale: 0.5, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.5, opacity: 0 }}
                              className="bg-secondary text-white text-xs rounded-full h-6 w-6 flex items-center justify-center"
                            >
                              {cartCount}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </Link>
                      <Link 
                        to="/orders" 
                        className="text-lg font-medium hover:text-primary transition-colors"
                        onClick={closeMenu}
                      >
                        Orders
                      </Link>
                      <Link 
                        to="/profile" 
                        className="text-lg font-medium hover:text-primary transition-colors"
                        onClick={closeMenu}
                      >
                        Profile
                      </Link>
                    </>
                  )}
                  <button 
                    onClick={handleLogout}
                    className="text-lg font-medium flex items-center gap-3 text-destructive hover:opacity-80 transition-opacity text-left"
                  >
                    <LogOut className="h-5 w-5" />
                    Logout
                  </button>
                </>
              ) : (
                <Button asChild className="w-full rounded-full bg-primary hover:bg-primary/90 text-white py-6">
                  <Link to="/login" onClick={closeMenu}>
                    <User className="h-5 w-5 mr-2" />
                    Login
                  </Link>
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <filter id="remove-white">
          <feColorMatrix type="matrix" values="1 0 0 0 0
                                               0 1 0 0 0
                                               0 0 1 0 0
                                               -1 -1 -1 3 0" />
        </filter>
      </svg>
    </nav>
  );
}
